import React, { useCallback, useEffect, useState } from "react";
import { Col, Form, Modal, Row, Select, Space, Spin } from "antd";
import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import NGIInput from "components/Common/inputs/NGIInput";
import Title from "antd/lib/typography/Title";
import NGIFormItem from "components/Common/form/NGIFormItem";
import { Option } from "antd/lib/mentions";
import NGISelect from "components/Common/inputs/NGISelect";
import { fetchAssetTypesList, fetchFirmwares, generateMockAssets, getAccessoryByType } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import { ASSET_CONDITIONS, ASSET_GROUPS, getAdditionalData } from "pages/tools/constants";
import { USER_TENANT_NAME } from "utils/constants";
import { uniq } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";
import { getDictionary } from "store/dictionaries/actions";
import { DICTIONARY_TYPES } from "store/dictionaries/constants";
import {
  ACCESSORY_TYPES,
  MAC_ADDRESS_VALIDATION_RULES,
  VALIDATION_RULES,
} from "pages/meter-management/upload-templates/addManualMeter/constants";
import { extractPromisesResult } from "utils/helpers/functions";
import { NO_SPACE_PATTERN } from "pages/meter-management/configuration/meters/variables";
import NGIDatePicker from "components/Common/inputs/NGIDatePicker";
import NGITable from "components/Common/NGITable";
import { textFormatter } from "components/Common/formatter";
import CustomLink from "components/Common/buttons/CustomLink";

const useStyles = makeStyles({
  titleWithDescription: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  modalContent: {
    padding: "30px 0",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    borderLeft: "none",
    borderRight: "none",
    marginBottom: 24,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  formItem: {
    marginTop: 15,
  },
});

const MockAssetsModal = ({ isOpen, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [isLoading, setLoader] = useState(false);
  const [isPXType, setIsPXType] = useState(false);
  const [condition, setCondition] = useState(null);
  const [assetGroup, setAssetGroup] = useState(null);
  const [results, setResults] = useState([]);
  const [dictionaries, setDictionaries] = useState({
    sensors: [],
    microcontrollers: [],
    firmwares: [],
    types: [],
  });

  const {
    list: { depot, supplier, batteryModel, simCardType, stoveSupplier, stoveModel },
  } = useSelector(DictionariesSelector);

  const onOk = useCallback(() => setResults([]), []);

  const onSubmit = useCallback(values => {
    setLoader(true);
    const { assetGroup, quantity } = values;
    const data = getAdditionalData(assetGroup, values);
    generateMockAssets(assetGroup, quantity, {
      ...data,
      ...(values.depot ? { depot: values.depot } : {}),
    })
      .then(({ data }) => {
        successMessage(`Pika Pika Chuu! Added ${values.quantity} ${values.assetGroup} with ${values.type}`);
        setResults(data?.result);
        setLoader(false);
      })
      .catch(e => {
        defaultNgiErrorMethod(e);
        setLoader(false);
      });
  }, []);

  const handleChange = useCallback(meterType => setIsPXType(meterType === "Smart Meter - PX"), []);
  const handleConditionChange = useCallback(condition => setCondition(condition), []);

  useEffect(() => {
    Promise.allSettled([
      getAccessoryByType(ACCESSORY_TYPES.SENSOR),
      getAccessoryByType(ACCESSORY_TYPES.MICROCONTROLLER),
      fetchFirmwares(),
      fetchAssetTypesList(),
    ]).then(data => {
      const [sensors, microcontrollers, firmwares, types] = extractPromisesResult(data);
      setDictionaries({
        sensors: sensors?.data?.result,
        microcontrollers: microcontrollers?.data?.result,
        firmwares: firmwares?.data?.result,
        types: types?.data?.result,
      });
    });
    dispatch(getDictionary(DICTIONARY_TYPES.BATTERY_MODEL));
    dispatch(getDictionary(DICTIONARY_TYPES.METER_TYPE));
    dispatch(getDictionary(DICTIONARY_TYPES.SIM_CARD_TYPE));
    dispatch(getDictionary(DICTIONARY_TYPES.DEPOT));
    dispatch(getDictionary(DICTIONARY_TYPES.SUPPLIER));
    dispatch(getDictionary(DICTIONARY_TYPES.STOVE_SUPPLIER));
    dispatch(getDictionary(DICTIONARY_TYPES.STOVE_MODEL));
  }, []);

  return (
    <Modal
      title={<div className={classes.titleWithDescription}>New Mock Assets</div>}
      open={isOpen}
      closable={false}
      footer={[]}
    >
      {!results.length ? (
        <Spin spinning={isLoading}>
          <Form form={form} name="new-mock-modal" layout="inline" onFinish={onSubmit} className={classes.formContainer}>
            <div className={classes.modalContent}>
              <Row>
                <Col span={12}>
                  <Title level={5}>Asset type*</Title>
                  <NGIFormItem name="assetGroup" rules={[{ required: true, message: "Please select asset type" }]}>
                    <NGISelect onChange={setAssetGroup} showSearch placeholder="Please asset type">
                      {ASSET_GROUPS.map(item => (
                        <Option key={item} value={item}>
                          {item}
                        </Option>
                      ))}
                    </NGISelect>
                  </NGIFormItem>
                </Col>
                <Col span={12}>
                  <Title level={5}>Type*</Title>
                  <NGIFormItem name="type" rules={[{ required: true, message: "Please select type" }]}>
                    <NGISelect
                      onChange={handleChange}
                      disabled={!assetGroup}
                      showSearch
                      placeholder="Please select type"
                    >
                      {dictionaries.types
                        ?.filter(item => item.group === assetGroup)
                        ?.map(item => (
                          <Option key={item.name} value={item.name}>
                            {item.name}
                          </Option>
                        ))}
                    </NGISelect>
                  </NGIFormItem>
                </Col>
              </Row>
              <Row className={classes.formItem}>
                <Col span={12}>
                  <Title level={5}>Condition*</Title>
                  <NGIFormItem name="condition" rules={[{ required: true, message: "Please select condition" }]}>
                    <NGISelect
                      onChange={handleConditionChange}
                      disabled={!assetGroup}
                      showSearch
                      placeholder="Please select condition"
                    >
                      {ASSET_CONDITIONS[assetGroup]?.map(item => (
                        <Option key={item} value={item}>
                          {item}
                        </Option>
                      ))}
                    </NGISelect>
                  </NGIFormItem>
                </Col>
                <Col span={12}>
                  <Title level={5}>Quantity*</Title>
                  <NGIFormItem name="quantity" rules={[{ required: true, message: "Please enter quantity" }]}>
                    <NGIInput type={"number"} placeholder="Enter Quantity" />
                  </NGIFormItem>
                </Col>
              </Row>
              <Row className={classes.formItem}>
                <Col span={24}>
                  <Title level={5}>Depot</Title>
                  <NGIFormItem name="depot">
                    <NGISelect allowClear showSearch placeholder={"Please select depot"}>
                      {depot[USER_TENANT_NAME()] &&
                        uniq(depot[USER_TENANT_NAME()])
                          ?.sort()
                          ?.map(item => (
                            <Option key={item} value={item}>
                              {item}
                            </Option>
                          ))}
                    </NGISelect>
                  </NGIFormItem>
                </Col>
              </Row>

              {assetGroup === "Cylinder" && (
                <Row className={classes.formItem}>
                  <Col span={12}>
                    <Title level={5}>Total Weight*</Title>
                    <NGIFormItem name="totalWeight" rules={[{ required: true, message: "Please enter total weight" }]}>
                      <NGIInput type="number" min={10} max={40} placeholder="Enter Total Weight" />
                    </NGIFormItem>
                  </Col>
                  <Col span={12}>
                    <Title level={5}>Supplier*</Title>
                    <NGIFormItem name="supplier" rules={[{ required: true, message: "Please select supplier" }]}>
                      <NGISelect showSearch placeholder={"Please select supplier"}>
                        {supplier?.map(item => (
                          <Option key={item} value={item}>
                            {item}
                          </Option>
                        ))}
                      </NGISelect>
                    </NGIFormItem>
                  </Col>
                </Row>
              )}

              {assetGroup === "Stove" && (
                <>
                  <Row className={classes.formItem}>
                    <Col span={12}>
                      <Title level={5}>Supplier*</Title>
                      <NGIFormItem name="supplier" rules={[{ required: true, message: "Please select supplier" }]}>
                        <NGISelect showSearch placeholder={"Please select supplier"}>
                          {stoveSupplier[USER_TENANT_NAME()] &&
                            stoveSupplier[USER_TENANT_NAME()]?.map(item => (
                              <Option key={item} value={item}>
                                {item}
                              </Option>
                            ))}
                        </NGISelect>
                      </NGIFormItem>
                    </Col>
                    <Col span={12}>
                      <Title level={5}>Model*</Title>
                      <NGIFormItem name="model" rules={[{ required: true, message: "Please select model" }]}>
                        <NGISelect showSearch placeholder={"Please select model"}>
                          {stoveModel[USER_TENANT_NAME()] &&
                            stoveModel[USER_TENANT_NAME()]?.map(item => (
                              <Option key={item} value={item}>
                                {item}
                              </Option>
                            ))}
                        </NGISelect>
                      </NGIFormItem>
                    </Col>
                  </Row>
                  <Row className={classes.formItem}>
                    <Col span={24}>
                      <Title level={5}>GRN*</Title>
                      <NGIFormItem name="GRN" rules={[{ required: true, message: "Please GRN" }]}>
                        <NGIInput placeholder="Enter GRN" />
                      </NGIFormItem>
                    </Col>
                  </Row>
                  {condition === "Refurbished" && (
                    <Row className={classes.formItem}>
                      <Col span={24}>
                        <Title level={5}>Refurbish By*</Title>
                        <NGIFormItem name="refurbishSupplier" rules={[{ required: true, message: "Please GRN" }]}>
                          <NGISelect showSearch placeholder={"Please select supplier"}>
                            {stoveSupplier[USER_TENANT_NAME()] &&
                              stoveSupplier[USER_TENANT_NAME()]?.map(item => (
                                <Option key={item} value={item}>
                                  {item}
                                </Option>
                              ))}
                          </NGISelect>
                        </NGIFormItem>
                      </Col>
                    </Row>
                  )}
                </>
              )}
              {assetGroup === "Meter" && (
                <>
                  <Row className={classes.formItem}>
                    <Col span={12}>
                      <Title level={5}>Meter Barcode*</Title>
                      <NGIFormItem
                        name="barcode"
                        rules={[
                          ...VALIDATION_RULES,
                          {
                            pattern: /^[A-Z0-9]+$/,
                            message: "Please match the requested format. Only A-Z or 0-9 characters are allowed.",
                          },
                          {
                            max: 30,
                            message: "The max length is 30",
                          },
                        ]}
                      >
                        <NGIInput disablePattern placeholder="TST7984928" />
                      </NGIFormItem>
                    </Col>
                    <Col span={12}>
                      <Title level={5}>Serial{!isPXType && "*"}</Title>
                      <NGIFormItem
                        name="serial"
                        rules={[
                          ...(isPXType ? [] : VALIDATION_RULES),
                          {
                            pattern: /^[A-Z0-9-]+$/,
                            message: "Please match the requested format. Only -, A-Z or 0-9 characters are allowed.",
                          },
                          {
                            max: 30,
                            message: "The max length is 30",
                          },
                        ]}
                      >
                        <NGIInput disablePattern placeholder="NG-WX-202310038275" />
                      </NGIFormItem>
                    </Col>
                  </Row>
                  <Row className={classes.formItem}>
                    <Col span={12}>
                      <Title level={5}>Pcb*</Title>
                      <NGIFormItem
                        name="pcb"
                        rules={[
                          ...VALIDATION_RULES,
                          {
                            pattern: NO_SPACE_PATTERN,
                            message: "Space isn't allowed on begin and finish.",
                          },
                          {
                            max: 30,
                            message: "Please change the value. The max length is 30.",
                          },
                        ]}
                      >
                        <NGIInput disablePattern placeholder="NWVF_202310017391" />
                      </NGIFormItem>
                    </Col>
                    <Col span={12}>
                      <Title level={5}>Sensor Serial{!isPXType && "*"}</Title>
                      <NGIFormItem
                        name="sensorSerial"
                        rules={[
                          ...(isPXType ? [] : VALIDATION_RULES),
                          {
                            pattern: /^[A-Z0-9]+$/,
                            message: "Please match the requested format. Only A-Z or 0-9 characters are allowed.",
                          },
                          {
                            max: 30,
                            message: "The max length is 30",
                          },
                        ]}
                      >
                        <NGIInput disablePattern placeholder="WC20230731508" />
                      </NGIFormItem>
                    </Col>
                  </Row>
                  <Row className={classes.formItem}>
                    <Col span={12}>
                      <Title level={5}>Battery 1</Title>
                      <NGIFormItem
                        name="battery1"
                        rules={[
                          {
                            pattern: /^[A-Z0-9]+$/,
                            message: "Please match the requested format. Only A-Z or 0-9 characters are allowed.",
                          },
                          {
                            max: 30,
                            message: "The max length is 30",
                          },
                        ]}
                      >
                        <NGIInput name="battery1" placeholder="B625394700" />
                      </NGIFormItem>
                    </Col>
                    <Col span={12}>
                      <Title level={5}>Battery 2</Title>
                      <NGIFormItem
                        name="battery2"
                        rules={[
                          {
                            pattern: /^[A-Z0-9]+$/,
                            message: "Please match the requested format. Only A-Z or 0-9 characters are allowed.",
                          },
                          {
                            max: 30,
                            message: "The max length is 30",
                          },
                        ]}
                      >
                        <NGIInput name="battery2" placeholder="B881511030" />
                      </NGIFormItem>
                    </Col>
                  </Row>
                  <Row className={classes.formItem}>
                    <Col span={24}>
                      <Title level={5}>Removable SimCard Iccid</Title>
                      <NGIFormItem
                        name="removableSimCardIccid"
                        rules={[
                          {
                            pattern: NO_SPACE_PATTERN,
                            message: "Space isn't allowed on begin and finish.",
                          },
                          {
                            max: 20,
                            message: "Please change the value. The max length is 20.",
                          },
                        ]}
                      >
                        <NGIInput placeholder="10819270698437962000" />
                      </NGIFormItem>
                    </Col>
                  </Row>

                  <Row className={classes.formItem}>
                    <Col span={12}>
                      <Title level={5}>ICCID</Title>
                      <NGIFormItem
                        name="iccid"
                        rules={[
                          {
                            pattern: /^(89)\d+$/,
                            message: "Please match the requested format. ICCID always starts with 89.",
                          },
                          { min: 18, max: 20, message: "The max length is from 18 to 20." },
                        ]}
                      >
                        <NGIInput placeholder="89457013333362835000" />
                      </NGIFormItem>
                    </Col>
                    <Col span={12}>
                      <Title level={5}>MSISDN</Title>
                      <NGIFormItem
                        name="msisdn"
                        rules={[
                          {
                            pattern: NO_SPACE_PATTERN,
                            message: "Space isn't allowed on begin and finish.",
                          },
                          {
                            max: 13,
                            message: "Please change the value. The max length is 13.",
                          },
                        ]}
                      >
                        <NGIInput placeholder={"Please enter MSISDN"} />
                      </NGIFormItem>
                    </Col>
                  </Row>
                  <Row className={classes.formItem}>
                    <Col span={12}>
                      <Title level={5}>IMSI</Title>
                      <NGIFormItem
                        name="imsi"
                        rules={[
                          {
                            pattern: NO_SPACE_PATTERN,
                            message: "Space isn't allowed on begin and finish.",
                          },
                          {
                            max: 15,
                            message: "Please change the value. The max length is 15.",
                          },
                        ]}
                      >
                        <NGIInput placeholder={"Please enter IMSI"} />
                      </NGIFormItem>
                    </Col>
                    <Col span={12}>
                      <Title level={5}>IP</Title>
                      <NGIFormItem
                        name="ip"
                        rules={[
                          {
                            pattern: /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)\.?\b){4}$/,
                            message: "Please match the requested format. For example, 192.168.0.15",
                          },
                        ]}
                      >
                        <NGIInput placeholder={"Please enter IP"} />
                      </NGIFormItem>
                    </Col>
                  </Row>

                  <Row className={classes.formItem}>
                    <Col span={24}>
                      <Title level={5}>Sensor Type</Title>
                      <NGIFormItem name="sensorType">
                        <NGISelect showSearch placeholder={"Please select sensor type"}>
                          {dictionaries.sensors.map(item => (
                            <Select.Option key={item.type} value={item.type}>
                              {item.type}
                            </Select.Option>
                          ))}
                        </NGISelect>
                      </NGIFormItem>
                    </Col>
                  </Row>
                  <Row className={classes.formItem}>
                    <Col span={12}>
                      <Title level={5}>Embedded SimCard Iccid </Title>
                      <NGIFormItem
                        name="embeddedSimCardIccid"
                        rules={[
                          {
                            pattern: NO_SPACE_PATTERN,
                            message: "Space isn't allowed on begin and finish.",
                          },
                          {
                            max: 20,
                            message: "Please change the value. The max length is 20.",
                          },
                        ]}
                      >
                        <NGIInput placeholder="10819270698437962789" />
                      </NGIFormItem>
                    </Col>
                    <Col span={12}>
                      <Title level={5}>SimCard Slots Number{!isPXType && "*"}</Title>
                      <NGIFormItem name="simCardSlotsNumber" rules={isPXType ? [] : VALIDATION_RULES}>
                        <NGISelect placeholder={"Please select slots number"}>
                          <Option value="1">1</Option>
                          <Option value="2">2</Option>
                        </NGISelect>
                      </NGIFormItem>
                    </Col>
                  </Row>
                  <Row className={classes.formItem}>
                    <Col span={24}>
                      <Title level={5}>SimCard Types{!isPXType && "*"}</Title>
                      <NGIFormItem name="simCardTypes" rules={isPXType ? [] : VALIDATION_RULES}>
                        <NGISelect
                          placeholder={"Please select SimCard types"}
                          style={{ width: "100%" }}
                          tokenSeparators={[","]}
                          mode="multiple"
                        >
                          {simCardType?.map(item => (
                            <Select.Option key={item} value={item}>
                              {item}
                            </Select.Option>
                          ))}
                        </NGISelect>
                      </NGIFormItem>
                    </Col>
                  </Row>
                  <Row className={classes.formItem}>
                    <Col span={12}>
                      <Title level={5}>Firmware{!isPXType && "*"}</Title>
                      <NGIFormItem name="firmware" rules={isPXType ? [] : VALIDATION_RULES}>
                        <NGISelect placeholder={"Please select firmware"}>
                          {dictionaries.firmwares.map(item => (
                            <Select.Option key={item.version} value={item.version}>
                              {item.version}
                            </Select.Option>
                          ))}
                        </NGISelect>
                      </NGIFormItem>
                    </Col>
                    <Col span={12}>
                      <Title level={5}>Controller*</Title>
                      <NGIFormItem name="controller" rules={[{ required: true, message: "Please enter value" }]}>
                        <NGISelect placeholder={"Please select controller"}>
                          {dictionaries.microcontrollers.map(item => (
                            <Select.Option key={item.type} value={item.type}>
                              {item.type}
                            </Select.Option>
                          ))}
                        </NGISelect>
                      </NGIFormItem>
                    </Col>
                  </Row>
                  <Row className={classes.formItem}>
                    <Col span={24}>
                      <Title level={5}>Battery Model{!isPXType && "*"}</Title>
                      <NGIFormItem name="batteryModel" rules={isPXType ? [] : VALIDATION_RULES}>
                        <NGISelect placeholder={"Please select battery model"}>
                          {batteryModel?.map(item => (
                            <Select.Option key={item} value={item}>
                              {item}
                            </Select.Option>
                          ))}
                        </NGISelect>
                      </NGIFormItem>
                    </Col>
                  </Row>
                  <Row className={classes.formItem}>
                    <Col span={12}>
                      <Title level={5}>Max Battery Voltage{!isPXType && "*"}</Title>
                      <NGIFormItem
                        name="maxBatteryVoltage"
                        rules={[
                          ...(isPXType ? [] : VALIDATION_RULES),
                          {
                            pattern: NO_SPACE_PATTERN,
                            message: "Space isn't allowed on begin and finish.",
                          },
                          {
                            validator: (_, value) => {
                              if (parseFloat(value) < 3 || parseFloat(value) > 4.3) {
                                return Promise.reject(
                                  "Please match the requested format. For example, XX.XX. The range is 3.00- 4.30."
                                );
                              }
                              return Promise.resolve();
                            },
                            message: "Please match the requested format. For example, XX.XX. The range is 3.00- 4.30.",
                          },
                        ]}
                      >
                        <NGIInput disablePattern placeholder={"Please enter Max Battery Voltage"} />
                      </NGIFormItem>
                    </Col>
                    <Col span={12}>
                      <Title level={5}>Min Battery Voltage{!isPXType && "*"}</Title>
                      <NGIFormItem
                        name="minBatteryVoltage"
                        rules={[
                          ...(isPXType ? [] : VALIDATION_RULES),
                          {
                            pattern: NO_SPACE_PATTERN,
                            message: "Space isn't allowed on begin and finish.",
                          },
                          {
                            validator: (_, value) => {
                              if (parseFloat(value) < 3 || parseFloat(value) > 4.3) {
                                return Promise.reject(
                                  "Please match the requested format. For example, XX.XX. The range is 3.00- 4.30."
                                );
                              }
                              return Promise.resolve();
                            },
                            message: "Please match the requested format. For example, XX.XX. The range is 3.00- 4.30.",
                          },
                        ]}
                      >
                        <NGIInput disablePattern placeholder={"Please enter Min Battery Voltage"} />
                      </NGIFormItem>
                    </Col>
                  </Row>
                  {isPXType && (
                    <>
                      <Row className={classes.formItem}>
                        <Col span={12}>
                          <Title level={5}>Modem MAC address</Title>
                          <NGIFormItem name="modemMac" rules={MAC_ADDRESS_VALIDATION_RULES}>
                            <NGIInput placeholder={"Please enter Modem MAC address"} />
                          </NGIFormItem>
                        </Col>
                        <Col span={12}>
                          <Title level={5}>Bluetooth Low Energy MAC address</Title>
                          <NGIFormItem name="bleMac" rules={MAC_ADDRESS_VALIDATION_RULES}>
                            <NGIInput placeholder={"Please enter Bluetooth Low Energy MAC address"} />
                          </NGIFormItem>
                        </Col>
                      </Row>

                      <Row className={classes.formItem}>
                        <Col span={12}>
                          <Title level={5}>Meter Assembly Date*</Title>
                          <NGIFormItem name="assemblyDate" rules={VALIDATION_RULES}>
                            <NGIDatePicker allowClear />
                          </NGIFormItem>
                        </Col>
                        <Col span={12}>
                          <Title level={5}>Certificate ID</Title>
                          <NGIFormItem name="certId">
                            <NGIInput placeholder={"Please enter Certificate ID"} />
                          </NGIFormItem>
                        </Col>
                      </Row>

                      <Row className={classes.formItem}>
                        <Col span={12}>
                          <Title level={5}>Secondary Controller</Title>
                          <NGIFormItem name="controller2">
                            <NGISelect placeholder={"Please select secondary controller"}>
                              {dictionaries.microcontrollers.map(item => (
                                <Select.Option key={item.type} value={item.type}>
                                  {item.type}
                                </Select.Option>
                              ))}
                            </NGISelect>
                          </NGIFormItem>
                        </Col>
                        <Col span={12}>
                          <Title level={5}>Secondary Firmware</Title>
                          <NGIFormItem name="mcu2Firmware">
                            <NGISelect placeholder={"Please select secondary firmware"}>
                              {dictionaries.firmwares.map(item => (
                                <Select.Option key={item.version} value={item.version}>
                                  {item.version}
                                </Select.Option>
                              ))}
                            </NGISelect>
                          </NGIFormItem>
                        </Col>
                      </Row>

                      <Row className={classes.formItem}>
                        <Col span={12}>
                          <Title level={5}>Modem Firmware</Title>
                          <NGIFormItem name="modemFirmware">
                            <NGISelect placeholder={"Please select modem firmware"}>
                              {dictionaries.firmwares.map(item => (
                                <Select.Option key={item.version} value={item.version}>
                                  {item.version}
                                </Select.Option>
                              ))}
                            </NGISelect>
                          </NGIFormItem>
                        </Col>
                        <Col span={12}>
                          <Title level={5}>Sensor Firmware</Title>
                          <NGIFormItem name="sensorFwVersion">
                            <NGIInput placeholder={"Please enter Sensor Firmware"} />
                          </NGIFormItem>
                        </Col>
                      </Row>

                      <Row className={classes.formItem}>
                        <Col span={12}>
                          <Title level={5}>Sensor Hardware</Title>
                          <NGIFormItem name="sensorHwVersion">
                            <NGIInput placeholder={"Please enter Sensor Hardware"} />
                          </NGIFormItem>
                        </Col>
                        <Col span={12}>
                          <Title level={5}>Battery Capacity</Title>
                          <NGIFormItem name="batteryCapacity">
                            <NGIInput
                              suffix={"mah"}
                              type="number"
                              step="any"
                              placeholder={"Please enter Battery Capacity"}
                            />
                          </NGIFormItem>
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              )}
            </div>
            <Space>
              <CustomButton onClick={onClose} type="primary" size="small" color="outlined" text="Cancel" />
              <CustomButton htmlType="submit" type="primary" text="GENERATE" size="small" />
            </Space>
          </Form>
        </Spin>
      ) : (
        <div>
          <div className={classes.modalContent}>
            <NGITable
              columns={[
                {
                  title: "ID",
                  dataIndex: "id",
                  render: cellContent => (
                    <CustomLink target="_blank" to={`warehouse/assets/${cellContent}`}>
                      {cellContent}
                    </CustomLink>
                  ),
                },
                {
                  title: "QrCode",
                  dataIndex: "qrCode",
                  render: textFormatter,
                },
              ]}
              data={{
                result: {
                  items: results,
                },
              }}
            />
          </div>
          <Space>
            <CustomButton onClick={onOk} type="primary" size="small" color="outlined" text="Ok" />
          </Space>
        </div>
      )}
    </Modal>
  );
};

export default MockAssetsModal;
